<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'AccountStatementReport',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
      default: null,
    },
    invoices: {
      type: Array,
      required: false,
      default: null,
    },
    transactionType: {
      type: String,
      required: false,
      default: null,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const accounts = ref(null);
  const formId = ref(null);
  const connector = ref(null);
  const item = ref({
    date_from: null,
    date_to: null,
    account: accounts.value ? accounts.value[0] : null,
  });
  const file = ref(null);
  const responseType = ref('json');
  const responseTypes = ref(['json', 'pdf']);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'full-screen',
  }));

  const $data = computed(() => ({
    accounts: accounts.value,
    formId: formId.value,
    connector: connector.value,
    item: item.value,
    file: file.value,
    responseType: responseType.value,
    responseTypes: responseTypes.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_accounts = () => {
    return $fn.value
      .fetch('list_account')
      .then((resp) =>
        _.orderBy(
          resp.data,
          ['inactive', 'account_type', 'name'],
          ['asc', 'desc', 'asc']
        )
      );
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_connector = () => {
    return $fn.value
      .resourceProxy({
        url: () =>
          $fn.value.urlFor('report_account_statement', {
            ...item.value,
            account: item.value.account?.id,
          }),
        useAuth: true,
      })
      .on('file', (args) => setData('file', args));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'accounts' },
      { name: 'formId' },
      { name: 'connector' },
      {
        name: 'item',
        cache: { type: 'local', key: 'AccountStatementReport.item' },
        dependsOn: ['accounts'],
      },
      { name: 'file' },
      {
        name: 'responseType',
        cache: { type: 'local', key: 'AccountStatementReport.responseType' },
      },
      { name: 'responseTypes' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      accounts: $$load_accounts,
      formId: $$load_formId,
      connector: $$load_connector,
    },
    ux: {},
    dataRefs: {
      accounts,
      formId,
      connector,
      item,
      file,
      responseType,
      responseTypes,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'AccountStatementReport/title',
        'Account Statement Report'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.invoices,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoices');
    }
  );
  watch(
    () => props.transactionType,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('transactionType');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_jtj3xkbtu_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_8527427120829392_setData_nmporjayy_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.date_from',
      value: $event.data[0],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_8527427120829392_setData_f3ufn0o9c_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.date_to',
      value: $event.data[1],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1729682679878258_script_8rw8wqnyw_value_callable = async (
    $event
  ) => {
    return $fn.value.withErrorHandler(connector.value.load());
  };
  const act_1729682679878258_script_8rw8wqnyw_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1729682679878258_script_8rw8wqnyw_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_jtj3xkbtu =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_jtj3xkbtu_getActionArgs,
    });
  const inst_evh_7315092382398562_close_1e5l4dugj =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_jtj3xkbtu,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_1e5l4dugj],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_0ctb9i422 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_6wcxc8zh9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_0ctb9i422,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_6wcxc8zh9],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_sgnlrld03 =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_7hr5igi09 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_sgnlrld03,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_7hr5igi09],
    event: 'close',
  });
  const act_8527427120829392_setData_nmporjayy = new actionTypes.SetDataAction({
    actionArgs: act_8527427120829392_setData_nmporjayy_getActionArgs,
  });
  const inst_evh_8527427120829392_changed_4rr9u4q5k =
    new actionTypes.EventHandlerImpl({
      action: act_8527427120829392_setData_nmporjayy,
      event: 'changed',
      displayName: 'setData',
    });
  const act_8527427120829392_setData_f3ufn0o9c = new actionTypes.SetDataAction({
    actionArgs: act_8527427120829392_setData_f3ufn0o9c_getActionArgs,
  });
  const inst_evh_8527427120829392_changed_op0xjllif =
    new actionTypes.EventHandlerImpl({
      action: act_8527427120829392_setData_f3ufn0o9c,
      event: 'changed',
      displayName: 'setData',
    });
  const inst_evh_8527427120829392_changed = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_8527427120829392_changed_4rr9u4q5k,
      inst_evh_8527427120829392_changed_op0xjllif,
    ],
    event: 'changed',
  });
  const act_1729682679878258_script_8rw8wqnyw = new actionTypes.ScriptAction({
    actionArgs: act_1729682679878258_script_8rw8wqnyw_getActionArgs,
  });
  const inst_evh_1729682679878258_click_kk7n99ez8 =
    new actionTypes.EventHandlerImpl({
      action: act_1729682679878258_script_8rw8wqnyw,
      event: 'click',
      displayName: 'script',
    });
  const inst_evh_1729682679878258_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1729682679878258_click_kk7n99ez8],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_1e5l4dugj,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_6wcxc8zh9,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_7hr5igi09,
    inst_evh_8564662037462133_close,
    inst_evh_8527427120829392_changed_4rr9u4q5k,
    inst_evh_8527427120829392_changed_op0xjllif,
    inst_evh_8527427120829392_changed,
    inst_evh_1729682679878258_click_kk7n99ez8,
    inst_evh_1729682679878258_click,
  };

  const evh_7315092382398562_close_1e5l4dugj = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_1e5l4dugj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_6wcxc8zh9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_6wcxc8zh9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_7hr5igi09 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_7hr5igi09.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8527427120829392_changed_4rr9u4q5k = async (event, scope) => {
    return await actions.inst_evh_8527427120829392_changed_4rr9u4q5k.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8527427120829392_changed_op0xjllif = async (event, scope) => {
    return await actions.inst_evh_8527427120829392_changed_op0xjllif.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8527427120829392_changed = async (event, scope) => {
    return await actions.inst_evh_8527427120829392_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1729682679878258_click_kk7n99ez8 = async (event, scope) => {
    return await actions.inst_evh_1729682679878258_click_kk7n99ez8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1729682679878258_click = async (event, scope) => {
    return await actions.inst_evh_1729682679878258_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="AccountStatementReport"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.payment.color"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" stretch v-if="loaded">
      <ItpBox direction="column" class="h-100 w-100">
        <div class="border-bottom">
          <ItpForm :id="formId" name="payment" class="pb-3" ref="mainForm">
            <ItpBox>
              <UcAccountSelector
                v-model="item.account"
                accountType="cash_book"
                class="me-2"
              >
              </UcAccountSelector>
              <DateRangeSelector
                name="paymentFilters"
                :from="item.date_from"
                :to="item.date_to"
                class="me-2"
                @changed="evh_8527427120829392_changed($event, {})"
              >
              </DateRangeSelector>
              <div>
                <ItpButton
                  :text="pgettext('Button', 'Display...')"
                  icon="ri:barcode-box-line"
                  :disabled="connector.loading"
                  :spinning="connector.loading"
                  @click="evh_1729682679878258_click($event, {})"
                >
                </ItpButton>
              </div>
            </ItpBox>
          </ItpForm>
        </div>
        <ItpPdfViewer :file="file && file.file" class="flex-fill">
        </ItpPdfViewer>
      </ItpBox>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded"> </UcFooter>
    </template>
  </ItpPage>
</template>
